import { useState, useEffect } from "react";
import {
  useCreateCompany,
  useOneCompany,
  useUpdateCompany,
} from "http-hooks/company/useCompany";
import { Switch } from "@headlessui/react";
import Input from "components/ui/Input";
import Select from "components/ui/Select";
import SelectStates from "components/ui/selectors/SelectStates";
import TextArea from "components/ui/TextArea";
import {
  BuildingOfficeIcon,
  MapPinIcon,
  PhoneIcon,
  DocumentTextIcon,
  BanknotesIcon,
  Cog6ToothIcon,
  CurrencyRupeeIcon,
  BuildingLibraryIcon,
  AdjustmentsHorizontalIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

const FormSkeleton = () => (
  <div className="animate-pulse space-y-4 p-6">
    {[...Array(4)].map((_, i) => (
      <div key={i} className="rounded-lg border border-gray-200 p-4">
        <div className="h-5 bg-gray-200 rounded w-1/4 mb-4"></div>
        <div className="grid grid-cols-2 gap-4">
          <div className="h-9 bg-gray-100 rounded"></div>
          <div className="h-9 bg-gray-100 rounded"></div>
        </div>
      </div>
    ))}
  </div>
);

const accountTypeOptions = [
  { value: 1, label: "Trader" },
  { value: 2, label: "Agent" },
];

const subscriptionTypeOptions = [
  { value: "G", label: "General" },
  { value: "P", label: "Plus" },
];

function Section({ icon: Icon, title, subtitle, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
      >
        <div className="flex items-center gap-3">
          {Icon && <Icon className="h-5 w-5 text-purple-600" />}
          <div className="text-left">
            <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
            {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
          </div>
        </div>
        <ChevronDownIcon
          className={classNames(
            "h-4 w-4 text-gray-500 transition-transform duration-200",
            open ? "rotate-180" : ""
          )}
        />
      </button>
      {open && <div className="px-4 py-4">{children}</div>}
    </div>
  );
}

export default function SetCompany({ id, onClose }) {
  const [selectedState, setSelectedState] = useState(null);
  const [selectedAccountType, setSelectedAccountType] = useState(null);
  const [selectedSubscriptionType, setSelectedSubscriptionType] = useState(null);
  const [companyDetails, setCompanyDetails] = useState({
    id_company: "",
    company_name: "",
    company_address1: "",
    company_address2: "",
    company_city: "",
    company_pin: "",
    company_state: "",
    company_stateid: "",
    contact_person: "",
    contact_no: "",
    company_email_id: "",
    company_copy_email_id: "",
    company_sender_email_id: "",
    company_gst: "",
    company_pan: "",
    ag_status: 1,
    termscond: "",
    splterm: "",
    slogan: "",
    ordpref: "",
    dealswith: "",
    acndetail: "",
    compfontstyle: "",
    acctype: "",
    company_password: "",
    maxusers: "",
    safecontact: "",
    subscription_type: "G",
    turnover_limit: 0,
    plantype: 0,
    company_status: 1,
    tcsperc: 0,
    party_cash_limit_fy: 0,
    sblitmtype: "G",
    cash_memo_height: "H",
    mrpref: "",
    bank_name: "",
    bank_ac_no: "",
    bank_ifsc: "",
    bank_ac_name: "",
    preferences: {
      shp: 1,
      clt: 1,
      sz: 1,
      lot: 1,
      packratio: 1,
      docuploader: 0,
      msg: 0,
      gd_bill_address: 1,
      cust_dashboard: 0,
      gst_change: 0,
      email_send_file: 0,
      disable_bill_entry_stock_less: 0,
      allow_scheme_order: 0,
    },
  });

  const { isLoading: isLoadingCompany, data } = useOneCompany(id);
  const { mutateAsync: mutateCreate, isPending: isCreating } = useCreateCompany();
  const { mutateAsync: mutateUpdate, isPending: isUpdating } = useUpdateCompany();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCompanyDetails((prev) => ({ ...prev, [name]: value }));
  };

  const handleStatusChange = (val) => {
    setCompanyDetails((prev) => ({ ...prev, ag_status: val ? 1 : 0 }));
  };

  const handlePreferenceChange = (e) => {
    const { name, checked } = e.target;
    setCompanyDetails((prev) => ({
      ...prev,
      preferences: { ...prev.preferences, [name]: checked ? 1 : 0 },
    }));
  };

  const handleStateChange = (selectedOption) => {
    setSelectedState(selectedOption);
    setCompanyDetails((prev) => ({
      ...prev,
      company_stateid: selectedOption?.value || "",
      company_state: selectedOption?.label || "",
    }));
  };

  const handleAccountTypeChange = (selectedOption) => {
    setSelectedAccountType(selectedOption);
    setCompanyDetails((prev) => ({
      ...prev,
      acctype: selectedOption?.value || "",
    }));
  };

  const handleSubscriptionTypeChange = (selectedOption) => {
    setSelectedSubscriptionType(selectedOption);
    setCompanyDetails((prev) => ({
      ...prev,
      subscription_type: selectedOption?.value || "G",
    }));
  };

  const handleSubmit = async () => {
    try {
      if (id) {
        await mutateUpdate({ id, data: companyDetails });
      } else {
        await mutateCreate(companyDetails);
      }
      onClose();
    } catch (error) {
      console.error("Company operation failed:", error);
    }
  };

  useEffect(() => {
    if (data) {
      setCompanyDetails((prev) => {
        const updatedDetails = {};
        Object.keys(prev).forEach((key) => {
          if (key === "preferences") {
            updatedDetails[key] = data.preferences ?? prev[key];
          } else {
            updatedDetails[key] = data[key] ?? prev[key];
          }
        });
        return updatedDetails;
      });

      if (data.company_stateid) {
        setSelectedState({ value: data.company_stateid, label: data.company_state });
      }
      if (data.acctype) {
        setSelectedAccountType(accountTypeOptions.find((o) => o.value === data.acctype));
      }
      if (data.subscription_type) {
        setSelectedSubscriptionType(
          subscriptionTypeOptions.find((o) => o.value === data.subscription_type)
        );
      }
    }
  }, [data]);

  if (id && isLoadingCompany) return <FormSkeleton />;

  const isSaving = isCreating || isUpdating;

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-3 mb-5">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-purple-100">
          <BuildingOfficeIcon className="h-5 w-5 text-purple-700" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-gray-900">
            {id ? "Update" : "Register New"} Company
          </h2>
          <p className="text-xs text-gray-500">
            {id
              ? "Modify company details and preferences"
              : "Fill in the details to create a new company with its database"}
          </p>
        </div>
      </div>

      <form className="space-y-3">
        {/* 1. Basic Information */}
        <Section icon={BuildingOfficeIcon} title="Basic Information" subtitle="Company name and ID">
          <div className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-6">
            <div className="sm:col-span-3">
              <Input
                type="number"
                name="id_company"
                label="Company ID *"
                value={companyDetails.id_company}
                onChange={handleInputChange}
                disabled={!!id}
                required
              />
            </div>
            <div className="sm:col-span-3">
              <Input
                type="text"
                name="company_name"
                label="Company Name *"
                maxLength="40"
                value={companyDetails.company_name}
                onChange={handleInputChange}
                required
              />
            </div>
          </div>
        </Section>

        {/* 2. Address Information */}
        <Section icon={MapPinIcon} title="Address" subtitle="Company physical location" defaultOpen={!!id}>
          <div className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-6">
            <div className="sm:col-span-3">
              <TextArea
                name="company_address1"
                label="Address Line 1"
                maxLength={40}
                value={companyDetails.company_address1}
                onChange={handleInputChange}
                isRequired
                rows={2}
              />
            </div>
            <div className="sm:col-span-3">
              <TextArea
                name="company_address2"
                label="Address Line 2"
                maxLength={40}
                value={companyDetails.company_address2}
                onChange={handleInputChange}
                rows={2}
              />
            </div>
            <div className="sm:col-span-2">
              <Input
                type="text"
                name="company_city"
                label="City *"
                maxLength="40"
                value={companyDetails.company_city}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="sm:col-span-2">
              <Input
                type="number"
                name="company_pin"
                label="PIN Code *"
                value={companyDetails.company_pin}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="sm:col-span-2">
              <SelectStates
                label="State *"
                value={selectedState}
                onChange={handleStateChange}
                isClearable={false}
              />
            </div>
          </div>
        </Section>

        {/* 3. Contact Information */}
        <Section icon={PhoneIcon} title="Contact Details" subtitle="People and communication" defaultOpen={!!id}>
          <div className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-6">
            <div className="sm:col-span-3">
              <Input
                type="text"
                name="contact_person"
                label="Contact Person *"
                maxLength="40"
                value={companyDetails.contact_person}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="sm:col-span-3">
              <Input
                type="text"
                name="contact_no"
                label="Contact Number *"
                maxLength="25"
                value={companyDetails.contact_no}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="sm:col-span-2">
              <Input
                type="email"
                name="company_email_id"
                label="Email *"
                maxLength="30"
                value={companyDetails.company_email_id}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="sm:col-span-2">
              <Input
                type="email"
                name="company_copy_email_id"
                label="CC Email"
                maxLength="30"
                value={companyDetails.company_copy_email_id}
                onChange={handleInputChange}
              />
            </div>
            <div className="sm:col-span-2">
              <Input
                type="email"
                name="company_sender_email_id"
                label="Sender Email"
                maxLength="30"
                value={companyDetails.company_sender_email_id}
                onChange={handleInputChange}
              />
            </div>
            <div className="sm:col-span-3">
              <Input
                type="text"
                name="safecontact"
                label="Dashboard Login Mobile"
                maxLength="11"
                value={companyDetails.safecontact}
                onChange={handleInputChange}
              />
            </div>
          </div>
        </Section>

        {/* 4. Tax & Legal */}
        <Section icon={DocumentTextIcon} title="Tax & Legal" subtitle="GST, PAN and identifiers" defaultOpen={!!id}>
          <div className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-6">
            <div className="sm:col-span-3">
              <Input
                type="text"
                name="company_gst"
                label="GST Number"
                maxLength="25"
                value={companyDetails.company_gst}
                onChange={handleInputChange}
              />
            </div>
            <div className="sm:col-span-3">
              <Input
                type="text"
                name="company_pan"
                label="PAN Number"
                maxLength="25"
                value={companyDetails.company_pan}
                onChange={handleInputChange}
              />
            </div>
          </div>
        </Section>

        {/* 5. System Configuration */}
        <Section icon={Cog6ToothIcon} title="System Configuration" subtitle="Account type, password, subscription">
          <div className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-6">
            <div className="sm:col-span-2">
              <Select
                options={accountTypeOptions}
                value={selectedAccountType}
                onChange={handleAccountTypeChange}
                label="Account Type *"
                placeholder="Select type"
                isClearable={false}
                isSearchable={false}
              />
            </div>
            <div className="sm:col-span-2">
              <Select
                options={subscriptionTypeOptions}
                value={selectedSubscriptionType}
                onChange={handleSubscriptionTypeChange}
                label="Subscription *"
                placeholder="Select plan"
                isClearable={false}
                isSearchable={false}
              />
            </div>
            <div className="sm:col-span-2">
              <Input
                type="number"
                name="maxusers"
                label="Max Users *"
                value={companyDetails.maxusers}
                onChange={handleInputChange}
                min="1"
                required
              />
            </div>
            <div className="sm:col-span-3">
              <Input
                type="text"
                name="company_password"
                label="Company Password *"
                maxLength="10"
                value={companyDetails.company_password}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="sm:col-span-3 flex items-end pb-1">
              <Input
                type="checkbox"
                name="plantype"
                label="Premium Plan"
                value={companyDetails.plantype}
                onChange={(e) =>
                  setCompanyDetails((prev) => ({ ...prev, plantype: e.target.checked ? 1 : 0 }))
                }
              />
            </div>
          </div>
        </Section>

        {/* 6. Financial Configuration */}
        <Section icon={CurrencyRupeeIcon} title="Financial Settings" subtitle="Turnover, TCS, cash limits" defaultOpen={!!id}>
          <div className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-6">
            <div className="sm:col-span-2">
              <Input
                type="number"
                name="turnover_limit"
                label="Turnover Limit"
                value={companyDetails.turnover_limit}
                onChange={handleInputChange}
                step="0.01"
                min="0"
              />
            </div>
            <div className="sm:col-span-2">
              <Input
                type="number"
                name="tcsperc"
                label="TCS %"
                value={companyDetails.tcsperc}
                onChange={handleInputChange}
                step="0.000001"
                min="0"
              />
            </div>
            <div className="sm:col-span-2">
              <Input
                type="number"
                name="party_cash_limit_fy"
                label="Party Cash Limit (FY)"
                value={companyDetails.party_cash_limit_fy}
                onChange={handleInputChange}
                step="0.01"
                min="0"
              />
            </div>
          </div>
        </Section>

        {/* 7. Document Prefixes */}
        <Section icon={DocumentTextIcon} title="Document Prefixes" subtitle="Order and receipt prefixes" defaultOpen={!!id}>
          <div className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-6">
            <div className="sm:col-span-2">
              <Input
                type="text"
                name="ordpref"
                label="Order Prefix"
                maxLength={4}
                value={companyDetails.ordpref}
                onChange={handleInputChange}
              />
            </div>
            <div className="sm:col-span-2">
              <Input
                type="text"
                name="mrpref"
                label="Money Receipt Prefix"
                maxLength={4}
                value={companyDetails.mrpref}
                onChange={handleInputChange}
              />
            </div>
          </div>
        </Section>

        {/* 8. Business Details */}
        <Section icon={BanknotesIcon} title="Business Details" subtitle="Terms, slogan, deals" defaultOpen={false}>
          <div className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-6">
            <div className="sm:col-span-3">
              <TextArea
                name="termscond"
                label="Terms & Conditions"
                value={companyDetails.termscond}
                onChange={handleInputChange}
                rows={3}
              />
            </div>
            <div className="sm:col-span-3">
              <TextArea
                name="splterm"
                label="Special Terms"
                maxLength={255}
                value={companyDetails.splterm}
                onChange={handleInputChange}
                rows={3}
              />
            </div>
            <div className="sm:col-span-3">
              <Input
                type="text"
                name="slogan"
                label="Slogan"
                maxLength="70"
                value={companyDetails.slogan}
                onChange={handleInputChange}
              />
            </div>
            <div className="sm:col-span-3">
              <TextArea
                name="dealswith"
                label="Deals With"
                maxLength={255}
                value={companyDetails.dealswith}
                onChange={handleInputChange}
                rows={2}
              />
            </div>
          </div>
        </Section>

        {/* 9. Bank Details */}
        <Section icon={BuildingLibraryIcon} title="Bank Details" subtitle="Banking information" defaultOpen={false}>
          <div className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-6">
            <div className="sm:col-span-3">
              <Input
                type="text"
                name="bank_name"
                label="Bank Name"
                maxLength="200"
                value={companyDetails.bank_name}
                onChange={handleInputChange}
              />
            </div>
            <div className="sm:col-span-3">
              <Input
                type="text"
                name="bank_ac_name"
                label="Account Holder Name"
                maxLength="100"
                value={companyDetails.bank_ac_name}
                onChange={handleInputChange}
              />
            </div>
            <div className="sm:col-span-3">
              <Input
                type="text"
                name="bank_ac_no"
                label="Account Number"
                maxLength="30"
                value={companyDetails.bank_ac_no}
                onChange={handleInputChange}
              />
            </div>
            <div className="sm:col-span-3">
              <Input
                type="text"
                name="bank_ifsc"
                label="IFSC Code"
                maxLength="30"
                value={companyDetails.bank_ifsc}
                onChange={handleInputChange}
              />
            </div>
          </div>
        </Section>

        {/* 10. Preferences */}
        <Section icon={AdjustmentsHorizontalIcon} title="Feature Preferences" subtitle="Toggle company features" defaultOpen={!!id}>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-3">
            {[
              { name: "shp", label: "Shape" },
              { name: "clt", label: "Color" },
              { name: "sz", label: "Size" },
              { name: "lot", label: "Lot" },
              { name: "packratio", label: "Pack Ratio" },
              { name: "docuploader", label: "Doc Uploader" },
              { name: "msg", label: "Messages" },
              { name: "gd_bill_address", label: "Godown Bill Addr" },
              { name: "cust_dashboard", label: "Customer Dashboard" },
              { name: "gst_change", label: "GST Change" },
              { name: "email_send_file", label: "Email Send File" },
              { name: "disable_bill_entry_stock_less", label: "Disable Bill If No Stock" },
              { name: "allow_scheme_order", label: "Allow Scheme Order" },
            ].map((pref) => (
              <div key={pref.name}>
                <Input
                  type="checkbox"
                  name={pref.name}
                  label={pref.label}
                  value={companyDetails.preferences[pref.name] === 1}
                  onChange={handlePreferenceChange}
                />
              </div>
            ))}
          </div>
        </Section>

        {/* Status toggle (edit mode only) */}
        {id && (
          <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold text-gray-900">Company Status</h3>
                <p className="text-xs text-gray-500">
                  {companyDetails.ag_status ? "This company is currently active" : "This company is currently inactive"}
                </p>
              </div>
              <Switch
                checked={!!companyDetails.ag_status}
                onChange={handleStatusChange}
                className={classNames(
                  companyDetails.ag_status ? "bg-purple-600" : "bg-gray-200",
                  "relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-purple-600 focus:ring-offset-2"
                )}
              >
                <span
                  aria-hidden="true"
                  className={classNames(
                    companyDetails.ag_status ? "translate-x-5" : "translate-x-0",
                    "pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
                  )}
                />
              </Switch>
            </div>
          </div>
        )}
      </form>

      {/* Sticky footer */}
      <div className="mt-4 flex items-center justify-end gap-x-3 border-t border-gray-200 pt-4">
        <button
          type="button"
          className="rounded-md px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors"
          onClick={onClose}
          disabled={isSaving}
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={isSaving}
          onClick={handleSubmit}
          className="rounded-md bg-purple-600 px-5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-purple-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isSaving ? "Saving..." : id ? "Update Company" : "Create Company"}
        </button>
      </div>
    </div>
  );
}
